import { Fragment, useContext, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { authProviders } from './AuthProviders';
import {
  UserSettingsAppearanceCard,
  UserSettingsIdentityCard,
  UserSettingsProfileCard,
} from '@backstage/plugin-user-settings';
import { UserNotificationSettingsCard } from '@backstage/plugin-notifications';
import UserRolesChecker from './tools/UserRolesChecker';
import { PluginStats } from './PluginStats';
import { TemplateStats } from './TemplateStats';
import AddExtensionCard from './tools/AddExtensionCard';
import {
  DevHubBox,
  DevHubButton,
  DevHubGrid,
  DevHubTabbedPage,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { Monitors } from './Monitors';
import { CloudAccountManager } from './tools/CloudAccountManager';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGroupMembership, UserContext } from '../../contexts/UserContext';
import MyFeatureToggles from '../../../components/Root/MyFeatureToggles';
import { InfoCard } from '@backstage/core-components';

import { AnnouncementsCard } from '@backstage-community/plugin-announcements';
import { AgentPromptSettings } from './AgentPromptSettings';
import { ConfigHelper, Feature } from '@devxdiscover/devhub-common';

const useStyles = makeStyles(() => ({
  authCard: {
    listStyle: 'none',
  },
}));

/**
 * Custom Settings Page Component for DevHub
 * @constructor
 */
export const Settings = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const tabPath = location.pathname.split('/').pop();
  const [hasPermissions, setHasPermissions] = useState(false);
  const { login, hasGroupMembership } = useContext(UserContext);

  const { value: groupData } = useGroupMembership(login, ['[CHQ -- DevHub]']);

  useEffect(() => {
    if (groupData && hasGroupMembership(groupData, '[CHQ -- DevHub]')) {
      setHasPermissions(true);
    }
  }, [groupData, hasGroupMembership]);

  const isNovaEnabled = ConfigHelper.instance.isFeatureEnabled(Feature.NOVA);

  const tabs = [
    {
      label: 'General',
      route: 'general',
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={6}>
            <UserSettingsProfileCard />
          </DevHubGrid>
          <DevHubGrid item xs={6}>
            <UserSettingsAppearanceCard />
          </DevHubGrid>
          <DevHubGrid item xs={6}>
            <UserSettingsIdentityCard />
          </DevHubGrid>
          <DevHubGrid item xs={6}>
            <InfoCard title="My Features" variant="gridItem">
              <MyFeatureToggles />
            </InfoCard>
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    {
      label: 'Auth Providers',
      route: 'auth-providers',
      content: (
        <Grid container>
          <Grid item xs={12} className={classes.authCard}>
            <Card>
              <CardContent>
                <Typography variant="h5">Auth Providers</Typography>
                <Typography variant="body1">{authProviders}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      ),
    },
    {
      label: 'Announcements',
      route: 'announcements',
      hasPermissions: hasPermissions,
      content: (
        <Fragment>
          <DevHubGrid container spacing={3}>
            <DevHubGrid item xs={12}>
              <DevHubBox display="flex" justifyContent="flex-end">
                <DevHubButton
                  variant="contained"
                  color="primary"
                  height="large"
                  onClick={() => {
                    window.location.href = '/announcements/create';
                  }}
                >
                  Create Announcement
                </DevHubButton>
              </DevHubBox>
            </DevHubGrid>
            <DevHubGrid item xs={12}>
              <AnnouncementsCard />
            </DevHubGrid>
          </DevHubGrid>
        </Fragment>
      ),
    },
    {
      label: 'Extensibility',
      route: 'extensibility',
      hasPermissions: hasPermissions,
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <DevHubBox>
              <DevHubTypography variant="h5">Plugins</DevHubTypography>
            </DevHubBox>
            <PluginStats />
          </DevHubGrid>
          <DevHubGrid item xs={12}>
            <DevHubBox>
              <DevHubTypography variant="h5">Templates</DevHubTypography>
            </DevHubBox>
            <TemplateStats />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    {
      label: 'Tools',
      route: 'tools',
      hasPermissions: hasPermissions,
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12} md={6}>
            <UserRolesChecker />
          </DevHubGrid>
          <DevHubGrid item xs={12} md={6}>
            <AddExtensionCard />
          </DevHubGrid>
          <DevHubGrid item xs={12} md={6}>
            <CloudAccountManager />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    {
      label: 'Monitors',
      route: 'monitors',
      hasPermissions: hasPermissions,
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <Monitors />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
    ...(isNovaEnabled
      ? [
          {
            label: 'Agent',
            route: 'agent',
            content: (
              <DevHubGrid container spacing={3}>
                <DevHubGrid item xs={12}>
                  <AgentPromptSettings hasAdminAccess={hasPermissions} />
                </DevHubGrid>
              </DevHubGrid>
            ),
          },
        ]
      : []),
    {
      label: 'Notifications',
      route: 'notifications',
      content: (
        <DevHubGrid container spacing={3}>
          <DevHubGrid item xs={12}>
            <UserNotificationSettingsCard
              originNames={{
                'plugin:scaffolder': 'Scaffolder',
              }}
            />
          </DevHubGrid>
        </DevHubGrid>
      ),
    },
  ];

  const getTabIndex = (path: string | undefined) => {
    const tabIndex = tabs.findIndex(tab => tab.route === path);
    return tabIndex >= 0 ? tabIndex : 0;
  };

  const initialTab = getTabIndex(tabPath);
  const [currentTab, setCurrentTab] = useState(initialTab);

  useEffect(() => {
    const newIndex = getTabIndex(tabPath);
    if (newIndex !== currentTab) {
      setCurrentTab(newIndex);
    } else if (!tabPath) {
      navigate(tabs[0].route, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabPath, navigate, currentTab, tabs]);
  return (
    <DevHubTabbedPage
      title="Settings"
      subtitle="Manage your preferences and configuration"
      tabs={tabs}
      navigate={navigate}
      currentTab={currentTab}
    />
  );
};
